import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useCreateStockInMutation, useListLposQuery, useGetLpoQuery } from "../../stockApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateStockIn, useCanCreateDirectStockIn } from "../../useStockPermissions";
import PartyPicker from "../../components/PartyPicker";
import LineItemsEditor, { emptyRow, computeTotals } from "../../components/LineItemsEditor";
import { paths } from "../../../../routes/paths";

const columns = { showRate: true, showVat: false, showRemarks: false, showReason: false, showMaxQty: true };
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

// Stock-In (GRN) - either against an approved LPO (lines prefill with the
// remaining orderedQty-receivedQty, capped there) or fully direct/ad-hoc
// (needs the separate Stock.CreateDirectStockIn permission - kept apart
// from Stock.CreateStockIn so it can't silently bypass the LPO approval
// gate). Arrives either blank (from the Stock-In list) or prefilled via
// location.state.fromLpo (the "Create Stock-In" shortcut on LpoDetail.jsx).
const StockInForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromLpo = location.state?.fromLpo;

  const canCreate = useCanCreateStockIn();
  const canDirect = useCanCreateDirectStockIn();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createStockIn, { isLoading: isSaving }] = useCreateStockInMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [branchId, setBranchId] = useState("");
  const [mode, setMode] = useState("lpo"); // "lpo" | "direct"
  const [selectedLpoGuid, setSelectedLpoGuid] = useState(fromLpo?.recordGuid || "");
  const [supplierOption, setSupplierOption] = useState(null);
  const [invoiceRef, setInvoiceRef] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [stockInDate, setStockInDate] = useState(toDateInput(new Date()));
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([emptyRow()]);
  const [seededFromLpo, setSeededFromLpo] = useState(false);

  const { data: openLpoPage } = useListLposQuery(
    { branchId: branchId ? Number(branchId) : undefined, pageSize: 200 },
    { skip: !branchId || mode !== "lpo" }
  );
  const openLpos = (openLpoPage?.items || []).filter((l) => ["APPROVED", "PARTIALLY_RECEIVED"].includes(l.status));

  const { data: selectedLpo } = useGetLpoQuery(selectedLpoGuid, { skip: !selectedLpoGuid });

  useEffect(() => {
    if (!selectedLpo) return;
    setBranchId(selectedLpo.branchId);
    setSupplierOption({
      value: selectedLpo.supplierId,
      label: selectedLpo.supplierName,
      customer: selectedLpo.supplierId != null
        ? { custId: selectedLpo.supplierId, branchId: selectedLpo.supplierBranchId, custName: selectedLpo.supplierName }
        : null,
    });
    if (!seededFromLpo || fromLpo) {
      setLines(
        selectedLpo.lines
          .filter((l) => Number(l.orderedQty) - Number(l.receivedQty) > 0)
          .map((l) => ({
            ...emptyRow(),
            productCode: l.productCode,
            productName: l.productName,
            unit: l.unit || "",
            qty: Number(l.orderedQty) - Number(l.receivedQty),
            rate: l.unitRate,
            maxQty: Number(l.orderedQty) - Number(l.receivedQty),
            locked: true,
            refId: l.lineId,
          }))
      );
      setSeededFromLpo(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLpo]);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setSelectedLpoGuid("");
    setSupplierOption(null);
    setSeededFromLpo(false);
    setLines([emptyRow()]);
  };

  const totals = useMemo(() => computeTotals(lines, columns), [lines]);
  const readOnly = mode === "direct" ? !canDirect : !canCreate;

  const handleSave = async () => {
    if (!branchId) return toast.error("Branch is required");
    const supplierName = (supplierOption?.customer?.custName || supplierOption?.label || "").trim();
    if (!supplierName) return toast.error("Supplier name is required");
    const validLines = lines.filter((l) => l.productCode && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one line with a product and quantity");

    const body = {
      branchId: Number(branchId),
      lpoRecordGuid: mode === "lpo" ? selectedLpoGuid || null : null,
      supplierId: supplierOption?.customer?.custId ?? null,
      supplierBranchId: supplierOption?.customer?.branchId ?? null,
      supplierName,
      invoiceRef: invoiceRef.trim() || null,
      invoiceDate: invoiceDate ? new Date(invoiceDate).toISOString() : null,
      stockInDate: new Date(stockInDate).toISOString(),
      totalAmount: totals.netAmount,
      remarks: remarks.trim() || null,
      lines: validLines.map((l) => ({
        lpoLineId: l.refId || null,
        productCode: Number(l.productCode),
        productName: l.productName,
        receivedQty: Number(l.qty),
        unit: l.unit || null,
        unitRate: Number(l.rate) || 0,
        lineAmount: (Number(l.qty) || 0) * (Number(l.rate) || 0),
      })),
    };

    try {
      const created = await createStockIn(body).unwrap();
      toast.success("Stock-In posted");
      navigate(paths.stockInDetail(created.recordGuid), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not post the Stock-In");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockIn} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Stock-In
              </Link>
              <h4>New Stock-In (GRN)</h4>
            </div>
          </div>
        </div>

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. Posting a {mode === "direct" ? "direct" : "LPO-linked"} Stock-In requires
            {" "}Stock.{mode === "direct" ? "CreateDirectStockIn" : "CreateStockIn"}.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex gap-4 mb-3">
              <div className="form-check">
                <input type="radio" className="form-check-input" id="mode-lpo" checked={mode === "lpo"} onChange={() => handleModeChange("lpo")} />
                <label className="form-check-label" htmlFor="mode-lpo">Against an LPO</label>
              </div>
              {canDirect && (
                <div className="form-check">
                  <input type="radio" className="form-check-input" id="mode-direct" checked={mode === "direct"} onChange={() => handleModeChange("direct")} />
                  <label className="form-check-label" htmlFor="mode-direct">Direct / Ad-hoc (no LPO)</label>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select className="form-control" value={branchId} onChange={(e) => { setBranchId(e.target.value); setSelectedLpoGuid(""); }} disabled={readOnly || (mode === "lpo" && Boolean(selectedLpoGuid))}>
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>

              {mode === "lpo" ? (
                <div className="col-md-5 mb-3">
                  <label className="form-label">LPO</label>
                  <select
                    className="form-control"
                    value={selectedLpoGuid}
                    onChange={(e) => { setSelectedLpoGuid(e.target.value); setSeededFromLpo(false); }}
                    disabled={readOnly || !branchId}
                  >
                    <option value="">--Select an approved LPO--</option>
                    {openLpos.map((l) => (
                      <option key={l.recordGuid} value={l.recordGuid}>{l.lpoNumber} - {l.supplierName}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="col-md-5 mb-3">
                  <label className="form-label">Supplier / Party (Customer Master)</label>
                  <PartyPicker mode="all-branches" value={supplierOption} onChange={setSupplierOption} disabled={readOnly} creatable />
                </div>
              )}

              <div className="col-md-2 mb-3">
                <label className="form-label">Invoice Ref</label>
                <input type="text" className="form-control" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Invoice Date</label>
                <input type="date" className="form-control" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Stock-In Date</label>
                <input type="date" className="form-control" value={stockInDate} onChange={(e) => setStockInDate(e.target.value)} disabled={readOnly} />
              </div>
            </div>

            <hr />
            <h5 className="mb-3">Received Lines</h5>
            <LineItemsEditor
              branchId={branchId ? Number(branchId) : undefined}
              rows={lines}
              onChange={setLines}
              columns={columns}
              disabled={readOnly || (mode === "lpo" && !selectedLpoGuid)}
            />

            <div className="row mt-3">
              <div className="col-md-8 mb-3">
                <label className="form-label">Remarks</label>
                <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3 d-flex align-items-end justify-content-end">
                <div>Total Amount: <strong>{totals.netAmount.toFixed(2)}</strong></div>
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.stockIn)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockInForm;
